import { FC, useRef } from "react";
import "./KeyPeople.scss";
import {
  CoreFieldValue,
  FieldFormV2Module,
  LoadingV2,
  SecondaryButton,
  SecondaryCard,
  SecondaryCardHeader,
} from "@ucl/library";
import { faTrash, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PersonInformation } from "@/types";
import { getFullName } from "../ComplexFields/PersonInformationComplexField";
import { KeyPerson } from "@/types/KeyPerson";
import { useKeyPerson } from "@/hooks/useKeyPerson";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import { FinancialApplicationFormSectionContextType } from "@/hooks/useFinancialApplicationFormSectionValidation";
import { KeyPersonForm } from "./KeyPersonForm";

export interface KeyPersonItemProps {
  financialApplicationId: string;
  keyPersonId: string;
  onDelete: (keyPersonId: string) => void;
  validationErrors: Record<string, string[]>;
  onApplicableRolesChange: (value: KeyPerson) => void;
  onBlur?: (field: CoreFieldValue) => void;
  isInitiallyCollapsed?: boolean;
  allowChangingWhetherCollapsed?: boolean;
  readOnly?: boolean;
  alreadyHasControlPersonRole: boolean;
  validationContextType: FinancialApplicationFormSectionContextType;
  shouldHideSecurityQuestions?: boolean;
  shouldAskIfCanProvideGovernmentIdInformation?: boolean;
  shouldAlwaysShowGovernmentIdInformationFields?: boolean;
  shouldShowDigitalBankingFields?: boolean;
  shouldShowDebitCardFields?: boolean;
}

export const KeyPersonItem: FC<KeyPersonItemProps> = (props) => {
  const fieldFormModule = useRef<FieldFormV2Module<KeyPerson> | null>(null);

  const {
    formValue,
    setFormValue,
    saveFormData,
    debitCardInformationRenderKey,
    digitalBankingInformationRenderKey,
  } = useKeyPerson(props.financialApplicationId, props.keyPersonId, props.onApplicableRolesChange);

  const { conditionalFlags } = useFinancialApplicationCoreData(props.financialApplicationId);

  if (!formValue) {
    return (
      <section className="key-person-form">
        <LoadingV2 />
      </section>
    );
  }

  return (
    <section className="key-person-form">
      <SecondaryCard
        hideBody={props.isInitiallyCollapsed ?? false}
        isCollapsible={props.allowChangingWhetherCollapsed ?? true}
        headerContent={
          <>
            <div className="key-person-form__header">
              <SecondaryCardHeader
                label={
                  getFullName(formValue?.personInformation as PersonInformation) || "New Key Person"
                }
              />
              {props.validationErrors && Object.keys(props.validationErrors).length > 0 && (
                <span className="key-person-form__validation-errors e-badge e-badge-pill">
                  {Object.keys(props.validationErrors).length}
                </span>
              )}
            </div>
            <div className="key-person-form__subheader">
              <ul className="key-person-form__subheader-applicable-roles">
                {formValue?.isBeneficialOwner && (
                  <li>
                    Beneficial Owner{" "}
                    {formValue.beneficialOwnerPercentage
                      ? `(${Number(formValue.beneficialOwnerPercentage).toFixed(2)}%)`
                      : ""}
                  </li>
                )}
                {formValue?.isAuthorizedSigner && <li>Authorized Signer</li>}
                {formValue?.isControlPerson && <li>Control Person</li>}
              </ul>
              {formValue.isApplicant && (
                <div className="key-person-form__applicant-badge" title="Applicant">
                  <FontAwesomeIcon icon={faUser} style={{ fontSize: "12px" }} />
                  <span className="key-person-form__applicant-text">Applicant</span>
                </div>
              )}
              {!props.readOnly && !formValue.isApplicant && (
                <SecondaryButton
                  minimal={true}
                  iconProps={{ icon: faTrash }}
                  cssClass="subtle danger-button"
                  onClick={() => {
                    props.onDelete(props.keyPersonId || "");
                  }}
                  disabled={props.readOnly}
                />
              )}
            </div>
          </>
        }
      >
        <KeyPersonForm
          fieldFormModule={fieldFormModule}
          formValue={formValue}
          setFormValue={setFormValue}
          saveFormData={saveFormData}
          debitCardInformationRenderKey={debitCardInformationRenderKey}
          digitalBankingInformationRenderKey={digitalBankingInformationRenderKey}
          conditionalFlags={conditionalFlags}
          validationErrors={props.validationErrors}
          onBlur={props.onBlur}
          readOnly={props.readOnly}
          alreadyHasControlPersonRole={props.alreadyHasControlPersonRole}
          validationContextType={props.validationContextType}
          shouldHideSecurityQuestions={props.shouldHideSecurityQuestions}
          shouldAskIfCanProvideGovernmentIdInformation={props.shouldAskIfCanProvideGovernmentIdInformation && !formValue.isApplicant}
          shouldAlwaysShowGovernmentIdInformationFields={props.shouldAlwaysShowGovernmentIdInformationFields}
          shouldShowDigitalBankingFields={props.shouldShowDigitalBankingFields}
          shouldShowDebitCardFields={props.shouldShowDebitCardFields}
        />
      </SecondaryCard>
    </section>
  );
};
